import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { nocService } from '../../services/apiService';

const NOCCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nocType: '',
    reason: '',
    additionalDetails: ''
  });

  const nocTypes = [
    { value: 'TRANSFER_CERTIFICATE', label: 'Transfer Certificate' },
    { value: 'COURSE_COMPLETION', label: 'Course Completion' },
    { value: 'INTERNSHIP', label: 'Internship' },
    { value: 'SCHOLARSHIP', label: 'Scholarship' },
    { value: 'EXAM_HALL_TICKET', label: 'Exam Hall Ticket' },
    { value: 'LIBRARY_CLEARANCE', label: 'Library Clearance' },
    { value: 'FEE_CLEARANCE', label: 'Fee Clearance' },
    { value: 'OTHER', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.nocType) {
      toast.error('Please select a NOC type');
      return;
    }
    
    // Reason is optional for EXAM_HALL_TICKET
    const isExamHallTicket = formData.nocType === 'EXAM_HALL_TICKET';
    
    if (!isExamHallTicket && !formData.reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    if (formData.reason.trim().length > 1000) {
      toast.error('Reason must not exceed 1000 characters');
      return;
    }

    setLoading(true);

    try {
      await nocService.createNOCRequest(formData);
      toast.success('NOC request submitted successfully!');
      navigate('/noc');
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        errors.forEach(err => toast.error(err.msg || err.message));
      } else {
        toast.error(error.response?.data?.message || 'Failed to submit NOC request');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-[calc(100vh-200px)] bg-slate-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">New NOC Request</h1>
          <p className="text-slate-600 mt-2">Submit a No Objection Certificate request</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                NOC Type *
              </label>
              <select
                name="nocType"
                required
                value={formData.nocType}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
              >
                <option value="">Select NOC Type</option>
                {nocTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                Reason {formData.nocType !== 'EXAM_HALL_TICKET' && '*'}
                {formData.nocType === 'EXAM_HALL_TICKET' && (
                  <span className="text-xs text-slate-500 ml-2">(Optional for Exam Hall Ticket)</span>
                )}
              </label>
              <textarea
                name="reason"
                required={formData.nocType !== 'EXAM_HALL_TICKET'}
                value={formData.reason}
                onChange={handleChange}
                rows={4}
                placeholder={formData.nocType === 'EXAM_HALL_TICKET' ? 'Optional - Add any additional details...' : 'Explain why you need this NOC...'}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow resize-vertical"
              />
              <div className="flex justify-between items-center mt-1">
                <p className="text-sm text-slate-500">
                  {formData.nocType === 'EXAM_HALL_TICKET'
                    ? 'Optional - Maximum 1000 characters'
                    : 'Maximum 1000 characters'
                  }
                </p>
                <p className={`text-sm ${
                  formData.reason.length > 1000 ? 'text-red-600 font-medium' : 'text-slate-500'
                }`}>
                  {formData.reason.length}/1000
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                Additional Details (Optional)
              </label>
              <textarea
                name="additionalDetails"
                value={formData.additionalDetails}
                onChange={handleChange}
                rows={3}
                placeholder="Any additional information that might help..."
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow resize-vertical ${
                  formData.additionalDetails.length > 2000 
                    ? 'border-red-300' 
                    : 'border-slate-300'
                }`}
              />
              <p className={`text-sm text-right mt-1 ${
                formData.additionalDetails.length > 2000 ? 'text-red-600 font-medium' : 'text-slate-500'
              }`}>
                {formData.additionalDetails.length}/2000
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">📋 Sequential Approval Workflow</h4>
              <p className="text-sm text-blue-800 mb-3">
                Your NOC request must be approved in the following order:
              </p>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Accounts Section - Fee clearance verification</li>
                <li>Library - Book return & dues clearance</li>
                <li>Head of Department (HOD) - Academic & departmental clearance</li>
                <li>Exam Section - Final approval & certificate issuance</li>
              </ol>
              <p className="text-sm text-blue-800 mt-3">
                Each department must approve before the next can review. You will receive email notifications at each approval stage.
              </p>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="spinner border-2 border-white border-t-transparent w-5 h-5"></div>
                    Submitting...
                  </span>
                ) : (
                  'Submit NOC Request'
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/noc')}
                className="px-8 py-3 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NOCCreate;
